import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Leaf, Users, Target, Handshake, ArrowRight, ShieldCheck, Recycle, MapPin, Phone, CheckCircle, TreeEvergreen, Truck, Medal, Heart, Lightbulb, Buildings } from '@phosphor-icons/react';
import axios from 'axios';
import { LocationsMap } from '../components/LocationsMap';
import { Seo } from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHY_NSEC_DEFAULTS = {
  why_nsec_heading: 'Why Choose NSEC Ltd?',
  why_nsec_description: 'We combine deep local knowledge with professional standards to deliver waste management services that make a genuine difference. Our team is committed to going above and beyond for every client.',
  why_nsec_image: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?q=80&w=800',
  coverage_heading: 'Serving the South West',
  coverage_description: 'Based in Weston-super-Mare, we provide our comprehensive range of services across North Somerset and the wider South West region. Our local presence means we understand the unique needs of businesses in our community.',
  coverage_areas: 'North Somerset,Bristol,Bath & NE Somerset,South Gloucestershire,Weston-super-Mare,Surrounding Areas',
  coverage_address: 'Weston-super-Mare, BS24',
  coverage_phone: '07917 092785',
  coverage_image: 'https://images.squarespace-cdn.com/content/v1/65f818232111306524528f63/2b5dfae9-338c-4047-9d29-71a43eb28790/coverage+map.JPG',
};

const DEFAULTS = {
  header_overline: "About Us",
  header_heading: "North Somerset Environment Company",
  header_description: "Since the inception of NSEC we have always strived to deliver the best to our communities in terms of environmental services, by being the local environment company of choice, employing local people, serving the local business and residential community and protecting the local environment.",
  mission_title: "Our Mission",
  mission_text: "To place the right resource, in the right place, at the right time, with the right tools, training and supervision to deliver an outstanding service first time to the residents and businesses of North Somerset.",
  vision_title: "Our Ambition",
  vision_text: "Our ambition is to lead the way in delivering environmentally focused services that contribute positively to the preservation and enhancement of North Somerset's natural surroundings. We are committed to offering innovative solutions that prioritise sustainability, conservation, and the well-being of our community.\n\nWe aim to deliver a service aligned with Environmental, Social, and Governance (ESG) principles, emphasising sustainability and responsible business practices in our operations.\n\nThrough our dedication to excellence and eco-conscious stewardship, we aim to be recognised as the foremost provider of environmental services in North Somerset and the local employer of choice. By fostering partnerships, embracing cutting-edge technologies, and empowering our team, we strive to create a greener, cleaner, and more sustainable future for generations to come."
};

const values = [
  { icon: ShieldCheck, title: 'Integrity', description: 'We conduct our business with honesty, transparency, and strong ethics.' },
  { icon: Lightbulb, title: 'Innovation', description: 'We embrace creativity and continuously seek new ways to improve our products and services, whilst delivering high-quality services through proficiency and skillful colleagues.' },
  { icon: Leaf, title: 'Sustainability', description: 'We are committed to ensuring long-term growth while minimizing environmental impact and cultivating social values.' },
  { icon: Users, title: 'Collaboration', description: 'We work together as a team and foster partnerships with our customers, suppliers, and communities.' },
  { icon: Heart, title: 'Respect', description: 'We encourage inclusivity, fairness, and open communication in the workplace, recognizing colleague worth, and acting with professionalism.' },
];

const strengths = [
  { icon: ShieldCheck, title: '99.95% Collection Rate', desc: 'Industry-leading collection success rate across all residential and business services.' },
  { icon: Truck, title: '115+ Specialist Vehicles', desc: 'A modern, diverse fleet to handle every environmental and waste service need.' },
  { icon: Medal, title: 'iESE Award Winners', desc: 'Recognised for excellence in the Service Change Project alongside North Somerset Council.' },
  { icon: TreeEvergreen, title: '108,000+ Properties Served', desc: 'Trusted by residential and business properties across the whole of North Somerset.' },
];

export default function AboutPage() {
  const [c, setC] = useState({ ...DEFAULTS, ...WHY_NSEC_DEFAULTS });
  const [locations, setLocations] = useState([]);
  useEffect(() => {
    axios.get(`${API}/page-content/about`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) setC(prev => ({ ...prev, ...r.data }));
    }).catch(() => {});
    axios.get(`${API}/analytics/locations`).then(r => setLocations(r.data)).catch(() => {});
  }, []);

  return (
    <main className="pt-24 bg-white" data-testid="about-page">
      <Seo title="About Us" description="North Somerset Environment Company — locally owned, environmentally focused, supporting our community since 2017." path="/about" />
      {/* Hero Section - matches Services / News format */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="accent-font text-lime-600 mb-4">{c.header_overline}</p>
            <h1 className="font-outfit font-bold text-4xl sm:text-5xl lg:text-6xl text-neutral-900 tracking-tight mb-6">
              {c.header_heading?.split(' ').slice(0, -1).join(' ')}{' '}
              <span className="text-lime-600">{c.header_heading?.split(' ').slice(-1)[0]}</span>
            </h1>
            <p className="text-neutral-600 text-lg max-w-3xl">
              {c.header_description}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision - side by side matching the user's screenshot style */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-outfit font-bold text-3xl lg:text-4xl text-neutral-900 mb-6 tracking-tight">
                Our <span className="text-lime-600">Mission</span>
              </h2>
              <div className="text-neutral-600 leading-relaxed space-y-4">
                {c.mission_text.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <h2 className="font-outfit font-bold text-3xl lg:text-4xl text-neutral-900 mb-6 tracking-tight">
                Our <span className="text-lime-600">Ambition</span>
              </h2>
              <div className="text-neutral-600 leading-relaxed space-y-4">
                {c.vision_text.split('\n\n').map((paragraph, i) => (
                  <p key={i}>{paragraph}</p>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 lg:py-24 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <p className="accent-font text-lime-600 mb-4">What Drives Us</p>
            <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight">
              Our Core <span className="text-lime-600">Values</span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {values.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-2xl border border-neutral-200 p-6 hover:border-lime-300 hover:shadow-lg transition-all duration-300"
                data-testid={`value-card-${i}`}
              >
                <div className="inline-flex p-3 rounded-xl bg-lime-50 mb-4">
                  <v.icon size={28} weight="duotone" className="text-lime-600" />
                </div>
                <h3 className="font-outfit font-semibold text-lg text-neutral-900 mb-2">{v.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{v.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose NSEC */}
      <section className="py-16 lg:py-24" data-testid="why-nsec-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="accent-font text-lime-600 mb-4">Why NSEC</p>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-6">
                {c.why_nsec_heading?.split(' ').slice(0, -2).join(' ')}{' '}
                <span className="text-lime-600">{c.why_nsec_heading?.split(' ').slice(-2).join(' ')}</span>
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-8">
                {c.why_nsec_description}
              </p>
              <div className="space-y-5">
                {strengths.map((s, i) => (
                  <motion.div
                    key={s.title}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="flex gap-4"
                  >
                    <div className="p-2.5 rounded-lg bg-lime-50 h-fit shrink-0">
                      <s.icon size={22} weight="duotone" className="text-lime-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-neutral-900 mb-0.5">{s.title}</h4>
                      <p className="text-sm text-neutral-600">{s.desc}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src={c.why_nsec_image}
                  alt="NSEC Team at Work"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 p-4 bg-lime-500 rounded-2xl shadow-lg">
                <Recycle size={48} weight="duotone" className="text-white" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Coverage Area */}
      <section className="py-16 lg:py-24 bg-neutral-50" data-testid="coverage-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="accent-font text-lime-600 mb-4">Our Coverage</p>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight mb-6">
                {c.coverage_heading?.split(' ').slice(0, -2).join(' ')}{' '}
                <span className="text-lime-600">{c.coverage_heading?.split(' ').slice(-2).join(' ')}</span>
              </h2>
              <p className="text-neutral-600 leading-relaxed mb-6">
                {c.coverage_description}
              </p>
              <div className="space-y-3 mb-6">
                <div className="grid grid-cols-2 gap-2">
                  {(c.coverage_areas || '').split(',').filter(Boolean).map((area) => (
                    <div key={area} className="flex items-center gap-2 text-neutral-600 text-sm">
                      <CheckCircle size={16} weight="fill" className="text-lime-500 shrink-0" />
                      <span>{area.trim()}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap gap-4 text-neutral-600">
                {c.coverage_address && (
                  <div className="flex items-center gap-2">
                    <MapPin size={18} className="text-lime-600" />
                    <span className="text-sm">{c.coverage_address}</span>
                  </div>
                )}
                {c.coverage_phone && (
                  <div className="flex items-center gap-2">
                    <Phone size={18} className="text-lime-600" />
                    <span className="text-sm">{c.coverage_phone}</span>
                  </div>
                )}
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              className="rounded-2xl overflow-hidden border border-neutral-200 shadow-lg"
            >
              <img
                src={c.coverage_image}
                alt="NSEC Coverage Map"
                className="w-full h-auto"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Locations */}
      {locations.length > 0 && (
        <section className="py-16 lg:py-24" data-testid="locations-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12"
            >
              <p className="accent-font text-lime-600 mb-4">Our Locations</p>
              <h2 className="font-outfit font-bold text-3xl sm:text-4xl text-neutral-900 tracking-tight">
                Where to <span className="text-lime-600">Find Us</span>
              </h2>
            </motion.div>

            <LocationsMap
              locations={locations}
              mapTestId="locations-map"
              cardTestIdPrefix="location-card"
            />
          </div>
        </section>
      )}

      {/* CTA - matches Services page format */}
      <section className="py-24 bg-neutral-900">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-outfit font-bold text-3xl lg:text-4xl text-white mb-6">
              Ready to Work With Us?
            </h2>
            <p className="text-neutral-300 text-lg mb-8">
              Get in touch today for a free, no-obligation quote tailored to your business needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2"
                data-testid="about-cta-contact"
              >
                Get a Free Quote
                <ArrowRight size={20} weight="bold" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-6 py-3 text-white border border-neutral-700 rounded-full hover:bg-neutral-800 transition-colors font-medium"
                data-testid="about-cta-services"
              >
                View Our Services
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
